/*! instant.page v5.2.0 - (C) 2019-2023 Alexandre Dieulot - https://instant.page/license */

let _chromiumMajorVersionInUserAgent = null;
let _allowQueryString;
let _allowExternalLinks;
let _useWhitelist;
let _delayOnHover = 65;
let _lastTouchTimestamp;
let _mouseoverTimer;
let _preloadedList = new Set();

const DELAY_TO_NOT_BE_CONSIDERED_A_TOUCH_INITIATED_ACTION = 1111;

init();

function init() {
  if (!document.createElement("link").relList.supports("prefetch")) return;

  const handleVaryAcceptHeader =
    "instantVaryAccept" in document.body.dataset || "Shopify" in window;

  const chromiumUserAgentIndex = navigator.userAgent.indexOf("Chrome/");

  if (chromiumUserAgentIndex > -1) {
    _chromiumMajorVersionInUserAgent = parseInt(
      navigator.userAgent.substring(chromiumUserAgentIndex + 7)
    );
  }

  if (
    handleVaryAcceptHeader &&
    _chromiumMajorVersionInUserAgent &&
    _chromiumMajorVersionInUserAgent < 110
  ) {
    return;
  }

  const mousedownShortcut = "instantMousedownShortcut" in document.body.dataset;

  _allowQueryString = "instantAllowQueryString" in document.body.dataset;
  _allowExternalLinks = "instantAllowExternalLinks" in document.body.dataset;
  _useWhitelist = "instantWhitelist" in document.body.dataset;

  const eventListenersOptions = {
    capture: true,
    passive: true
  };

  let useMousedown = false;
  let useMousedownOnly = false;
  let useViewport = false;

  if ("instantIntensity" in document.body.dataset) {
    const intensity = document.body.dataset.instantIntensity;

    if (intensity.startsWith("mousedown")) {
      useMousedown = true;

      if (intensity === "mousedown-only") {
        useMousedownOnly = true;
      }
    } else if (intensity.startsWith("viewport")) {
      const isNavigatorConnectionSaveDataEnabled =
        navigator.connection && navigator.connection.saveData;

      const isNavigatorConnectionLike2g =
        navigator.connection &&
        navigator.connection.effectiveType &&
        navigator.connection.effectiveType.includes("2g");

      if (
        !isNavigatorConnectionSaveDataEnabled &&
        !isNavigatorConnectionLike2g
      ) {
        if (intensity === "viewport") {
          if (
            document.documentElement.clientWidth *
              document.documentElement.clientHeight <
            450000
          ) {
            useViewport = true;
          }
        } else if (intensity === "viewport-all") {
          useViewport = true;
        }
      }
    } else {
      const milliseconds = parseInt(intensity);

      if (!isNaN(milliseconds)) {
        _delayOnHover = milliseconds;
      }
    }
  }

  if (useMousedownOnly || "ontouchstart" in window) {
    document.addEventListener(
      "touchstart",
      touchstartListener,
      eventListenersOptions
    );
  }

  if (useMousedown) {
    if (!mousedownShortcut) {
      document.addEventListener(
        "mousedown",
        mousedownListener,
        eventListenersOptions
      );
    }
  } else {
    document.addEventListener(
      "mouseover",
      mouseoverListener,
      eventListenersOptions
    );
  }

  if (mousedownShortcut) {
    document.addEventListener(
      "mousedown",
      mousedownShortcutListener,
      eventListenersOptions
    );
  }

  if (useViewport) {
    let requestIdleCallbackOrFallback = window.requestIdleCallback;

    if (!requestIdleCallbackOrFallback) {
      requestIdleCallbackOrFallback = function (callback) {
        callback();
      };
    }

    requestIdleCallbackOrFallback(
      function () {
        const intersectionObserver = new IntersectionObserver(function (
          entries
        ) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              const anchorElement = entry.target;
              intersectionObserver.unobserve(anchorElement);
              preload(anchorElement.href);
            }
          });
        });

        document.querySelectorAll("a").forEach(function (anchorElement) {
          if (isPreloadable(anchorElement)) {
            intersectionObserver.observe(anchorElement);
          }
        });
      },
      { timeout: 1500 }
    );
  }
}

function touchstartListener(event) {
  _lastTouchTimestamp = performance.now();

  if (!("closest" in event.target)) return;

  const anchorElement = event.target.closest("a");

  if (isPreloadable(anchorElement)) {
    preload(anchorElement.href, "high");
  }
}

function mouseoverListener(event) {
  if (
    performance.now() - _lastTouchTimestamp <
      DELAY_TO_NOT_BE_CONSIDERED_A_TOUCH_INITIATED_ACTION ||
    !("closest" in event.target)
  ) {
    return;
  }

  const anchorElement = event.target.closest("a");

  if (isPreloadable(anchorElement)) {
    anchorElement.addEventListener("mouseout", mouseoutListener, {
      passive: true
    });

    _mouseoverTimer = setTimeout(function () {
      preload(anchorElement.href, "high");
      _mouseoverTimer = undefined;
    }, _delayOnHover);
  }
}

function mousedownListener(event) {
  if (!("closest" in event.target)) return;

  const anchorElement = event.target.closest("a");

  if (isPreloadable(anchorElement)) {
    preload(anchorElement.href, "high");
  }
}

function mouseoutListener(event) {
  if (
    event.relatedTarget &&
    event.target.closest("a") === event.relatedTarget.closest("a")
  ) {
    return;
  }

  if (_mouseoverTimer) {
    clearTimeout(_mouseoverTimer);
    _mouseoverTimer = undefined;
  }
}

function mousedownShortcutListener(event) {
  if (
    performance.now() - _lastTouchTimestamp <
    DELAY_TO_NOT_BE_CONSIDERED_A_TOUCH_INITIATED_ACTION
  ) {
    return;
  }

  const anchorElement = event.target.closest("a");

  if (
    event.which > 1 ||
    event.metaKey ||
    event.ctrlKey ||
    !anchorElement
  ) {
    return;
  }

  anchorElement.addEventListener(
    "click",
    function (event2) {
      if (event2.detail !== 1337) {
        event2.preventDefault();
      }
    },
    {
      capture: true,
      passive: false,
      once: true
    }
  );

  const customEvent = new MouseEvent("click", {
    view: window,
    bubbles: true,
    cancelable: false,
    detail: 1337
  });

  anchorElement.dispatchEvent(customEvent);
}

function isPreloadable(anchorElement) {
  if (!anchorElement || !anchorElement.href) return false;

  if (_useWhitelist && !("instant" in anchorElement.dataset)) return false;

  if (
    anchorElement.origin !== location.origin &&
    (!(_allowExternalLinks || "instant" in anchorElement.dataset) ||
      !_chromiumMajorVersionInUserAgent)
  ) {
    return false;
  }

  if (!["http:", "https:"].includes(anchorElement.protocol)) return false;

  if (
    anchorElement.protocol === "http:" &&
    location.protocol === "https:"
  ) {
    return false;
  }

  if (
    !_allowQueryString &&
    anchorElement.search &&
    !("instant" in anchorElement.dataset)
  ) {
    return false;
  }

  if (
    anchorElement.hash &&
    anchorElement.pathname + anchorElement.search ===
      location.pathname + location.search
  ) {
    return false;
  }

  if ("noInstant" in anchorElement.dataset) return false;

  if (isShopifyBlockedUrl(anchorElement.href)) return false;

  return true;
}

function isShopifyBlockedUrl(url) {
  const blockedPaths = [
    "/cart",
    "/checkout",
    "/account",
    "/search",
    "/recommendations",
    "/apps/",
    "/tools/",
    "/challenge"
  ];

  return blockedPaths.some(function (path) {
    return url.includes(path);
  });
}

function preload(url, fetchPriority = "auto") {
  if (_preloadedList.has(url)) return;
  if (isShopifyBlockedUrl(url)) return;

  const linkElement = document.createElement("link");

  linkElement.rel = "prefetch";
  linkElement.href = url;
  linkElement.fetchPriority = fetchPriority;
  linkElement.as = "document";

  document.head.appendChild(linkElement);

  _preloadedList.add(url);
}